import { useMemo } from "react";
import { Row } from "../../misc/Row";
import { Student } from "../../providers/studentsProvider";
import { Money } from "../../misc/Money";
import { Column } from "../../misc/Column";
import { Box, Typography, useTheme } from "@mui/material";
import { useRecordContext } from "react-admin";
import { autovioColors } from "../../misc/backofficeTheme";
import { SendFirstDunningNoticeButton } from "../../buttons/SendFirstDunningNoticeButton";
import { SendSecondDunningNoticeButton } from "../../buttons/SendSecondDunningNoticeButton";
import { BlockStudentAndSendPaymentReminderButton } from "../../buttons/BlockStudentAndSendPaymentReminderButton";
import { HandoverToPairFinanceButton } from "../../buttons/HandoverToPairFinanceButton";
import { ActivateStudentButton } from "../../buttons/ActivateStudentButton";

export const DunningProcessTimeline = () => {
  const theme = useTheme();
  const student = useRecordContext<Student>();
  const { fulfilledSteps, nextStep } = useMemo(() => {
    const fulfilledSteps: (typeof dunningProcessSteps)[number][] = [dunningProcessSteps[0]];
    let nextStep: (typeof dunningProcessSteps)[number] = dunningProcessSteps[1];
    if (!student.dunningProcess) return { fulfilledSteps, nextStep };
    dunningProcessSteps.slice(1).forEach((step, index, stepsArr) => {
      if (step.dateKey && student.dunningProcess![step.dateKey]) {
        fulfilledSteps.push(step);
        nextStep = stepsArr[index + 1];
      }
    });
    return { fulfilledSteps, nextStep };
  }, [student]);

  return (
    <Row
      sx={{
        height: "55px",
        mb: "32px",
        width: "100%",
        border: 1,
        borderColor: "divider",
        borderRadius: "12px",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {_stages.map(({ key, title, renderSubtitle, renderAction }, index) => {
        const subTitle = renderSubtitle ? renderSubtitle(student) : null;
        const action = renderAction && (nextStep.key === key || key === "paid") ? renderAction(student) : null;
        const isFulfilledStage = fulfilledSteps.some((step) => step.key === key);
        const isLastFulfilledStage = isFulfilledStage && fulfilledSteps[fulfilledSteps.length - 1].key === key;
        const titleColor = isFulfilledStage ? theme.palette.common.white : theme.palette.text.disabled;
        const subTitleColor = isFulfilledStage ? titleColor : autovioColors.green;
        let backgroundColor = theme.palette.background.paper;
        if (key === "paid") {
          backgroundColor = theme.palette.action.hover;
        } else if (isFulfilledStage) {
          if (isLastFulfilledStage) {
            backgroundColor = autovioColors.red;
          } else {
            backgroundColor = "#f0a8a8";
          }
        }

        let arrowColor = theme.palette.background.paper;
        if (key === "paid") {
          arrowColor = theme.palette.action.hover;
        } else if (isFulfilledStage) {
          if (isLastFulfilledStage) {
            arrowColor = autovioColors.red;
          } else {
            arrowColor = "#f0a8a8";
          }
        }

        return (
          <Row
            key={key}
            sx={{
              flex: 1,
              height: "100%",
              alignItems: "center",
              backgroundColor,
            }}
          >
            {index > 0 && (
              <TriangleDivider
                arrowColor={arrowColor}
                borderColor={isFulfilledStage ? theme.palette.common.white : theme.palette.divider}
              />
            )}
            <Column
              sx={{
                flex: 1,
                alignItems: "center",
                // pl: "16px",
              }}
            >
              <Typography variant="body2" fontSize="14px" fontWeight={600} color={titleColor}>
                {title}
              </Typography>
              {subTitle ? (
                <Typography variant="subtitle1" fontSize="12px" color={subTitleColor}>
                  {subTitle}
                </Typography>
              ) : null}
              {action ? action : null}
            </Column>
          </Row>
        );
      })}
    </Row>
  );
};

const dunningProcessSteps = [
  { key: "negativeBalance", dateKey: undefined },
  { key: "blocked", dateKey: "paymentReminderSentAt" },
  { key: "reminder1", dateKey: "firstDunningNoticeSentAt" },
  { key: "reminder2", dateKey: "secondDunningNoticeSentAt" },
  { key: "inkasso", dateKey: "handedOverToPairFinanceAt" },
  { key: "paid", dateKey: undefined },
] as const;

interface _Stage {
  key: (typeof dunningProcessSteps)[number]["key"];
  title: string;
  renderAction?: (student: Student) => React.ReactNode;
  renderSubtitle?: (student: Student) => React.ReactNode;
}

const _stages: Array<_Stage> = [
  {
    key: dunningProcessSteps[0].key,
    title: "Offener Betrag",
    renderSubtitle: (student) => <Money cents={student.dunningProcess?.outstandingAmount || student.balance} />,
  },
  {
    key: dunningProcessSteps[1].key,
    title: "Blockiert",
    renderAction: (student) =>
      student.balance < 0 && <BlockStudentAndSendPaymentReminderButton variant="dialogButton" />,
    renderSubtitle: (student) => student.dunningProcess?.paymentReminderSentAt?.toFormat("dd.MM.yyyy"),
  },
  {
    key: dunningProcessSteps[2].key,
    title: "1. Mahnung",
    renderAction: (student) =>
      student?.dunningProcess?.outstandingAmount &&
      student.dunningProcess.outstandingAmount > 0 && <SendFirstDunningNoticeButton variant="dialogButton" />,
    renderSubtitle: (student: Student) => student.dunningProcess?.firstDunningNoticeSentAt?.toFormat("dd.MM.yyyy"),
  },
  {
    key: dunningProcessSteps[3].key,
    title: "2. Mahnung",
    renderAction: (student) =>
      student?.dunningProcess?.outstandingAmount &&
      student.dunningProcess.outstandingAmount > 0 && <SendSecondDunningNoticeButton variant="dialogButton" />,
    renderSubtitle: (student: Student) => student.dunningProcess?.secondDunningNoticeSentAt?.toFormat("dd.MM.yyyy"),
  },
  {
    key: dunningProcessSteps[4].key,
    title: "Inkasso",
    renderAction: (student) =>
      student?.dunningProcess?.outstandingAmount &&
      student.dunningProcess.outstandingAmount > 0 && <HandoverToPairFinanceButton variant="dialogButton" />,
    renderSubtitle: (student: Student) => student.dunningProcess?.handedOverToPairFinanceAt?.toFormat("dd.MM.yyyy"),
  },
  {
    key: dunningProcessSteps[5].key,
    title: "Bezahlt",
    renderAction: (student) =>
      (student.balance >= 0 || student.dunningProcess?.outstandingAmount === 0) && (
        <ActivateStudentButton variant="dialogButton" />
      ),
  },
];

const TriangleDivider = ({ arrowColor, borderColor }: { arrowColor: string; borderColor: string }) => (
  <Column sx={{ marginLeft: "-20px", border: 0, outline: 0 }}>
    <Row
      sx={{
        height: "56px",
        width: "20px",
        clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%, 18px 50%)",
        backgroundColor: borderColor,
        justifyContent: "flex-end",
      }}
    >
      <Box
        sx={{
          height: "56px",
          width: "18px",
          clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%, 100% 50%)",
          backgroundColor: arrowColor,
        }}
      />
    </Row>
  </Column>
);
